import { ActivatedRoute } from '@angular/router';
import { WorkflowContextService } from 'src/app/services';

export interface DataEntityChange {
  templateCode: string;
  action: 'add' | 'remove';
}

export interface ActivityChange {
  name: string;
  action: 'add' | 'remove';
  dataEntities: DataEntityChange[];
}

export interface MigrationSummary {
  addedActivities: Array<{ name: string; type: string }>;
  removedActivities: Array<{ name: string; type: string }>;
  addedDataEntities: Array<{ activityName: string; templateCode: string }>;
  removedDataEntities: Array<{ activityName: string; templateCode: string }>;
  workflowExists: boolean;
  errorMessages: string[];
  customLists: Array<{
    name: string;
    itemCount: number;
    attributeCount: number;
    alreadyExists: boolean;
    roleName?: string;
    roleExists: boolean;
  }>;
  roles: Array<{ name: string; alreadyExists: boolean }>;
}

export interface ApplicationDataValidationStatus {
  isValid: boolean;
  isValidating: boolean;
  recordCount: number;
  errors: string[];
  matchedProperties: string[];
  unmatchedProperties: string[];
  missingRequiredProperties: string[];
}

type MetadataValidationResultLike = {
  isValid?: boolean;
  errors?: string[];
  warnings?: string[];
  supportedTypes?: string[];
  unsupportedTypes?: string[];
};

const COMPATIBLE_DATA_ENTITY_TYPES = new Set(['today', 'signature']);
const COMPATIBLE_ACTIVITY_TYPES = new Set(['required-user', 'complete']);

function parseValidationTypeList(rawTypes: string): string[] {
  return rawTypes
    .split(',')
    .map(type => type.trim())
    .filter(type => !!type);
}

function dedupeTypes(types: string[]): string[] {
  const seen = new Set<string>();

  return types.filter(type => {
    const normalizedType = type.toLowerCase();
    if (seen.has(normalizedType)) {
      return false;
    }

    seen.add(normalizedType);
    return true;
  });
}

export function normalizeMigrationMetadataValidationResult<
  T extends MetadataValidationResultLike
>(result: T | null | undefined): Required<MetadataValidationResultLike> {
  const errors = [...(result?.errors || [])];
  const warnings = [...(result?.warnings || [])];
  const supportedTypes = [...(result?.supportedTypes || [])];
  const unsupportedTypes = [...(result?.unsupportedTypes || [])];
  const recognizedDataEntityTypes: string[] = [];

  const normalizedErrors = errors.flatMap(error => {
    const unsupportedActivityMatch = error.match(
      /^Found\s+\d+\s+unsupported activity type\(s\):\s*(.+)$/i
    );

    if (unsupportedActivityMatch) {
      const unsupportedActivityTypes = parseValidationTypeList(
        unsupportedActivityMatch[1]
      );
      const remainingActivityTypes = unsupportedActivityTypes.filter(
        type => !COMPATIBLE_ACTIVITY_TYPES.has(type.toLowerCase())
      );

      if (remainingActivityTypes.length === 0) {
        return [];
      }

      return [
        `Found ${remainingActivityTypes.length} unsupported activity type(s): ${remainingActivityTypes.join(
          ', '
        )}`
      ];
    }

    const unsupportedDataEntityMatch = error.match(
      /^Found\s+\d+\s+unsupported data entity type\(s\):\s*(.+)$/i
    );

    if (unsupportedDataEntityMatch) {
      const unsupportedDataEntityTypes = parseValidationTypeList(
        unsupportedDataEntityMatch[1]
      );
      const remainingDataEntityTypes = unsupportedDataEntityTypes.filter(type => {
        const isCompatible = COMPATIBLE_DATA_ENTITY_TYPES.has(
          type.toLowerCase()
        );

        if (isCompatible) {
          recognizedDataEntityTypes.push(type);
        }

        return !isCompatible;
      });

      if (remainingDataEntityTypes.length === 0) {
        return [];
      }

      return [
        `Found ${remainingDataEntityTypes.length} unsupported data entity type(s): ${remainingDataEntityTypes.join(
          ', '
        )}`
      ];
    }

    return [error];
  });

  const normalizedUnsupportedTypes = unsupportedTypes.filter(type => {
    const isCompatible = COMPATIBLE_DATA_ENTITY_TYPES.has(type.toLowerCase());

    if (isCompatible) {
      recognizedDataEntityTypes.push(type);
    }

    return !isCompatible;
  });

  return {
    isValid: normalizedErrors.length === 0,
    errors: normalizedErrors,
    warnings,
    supportedTypes: dedupeTypes([
      ...supportedTypes,
      ...recognizedDataEntityTypes
    ]),
    unsupportedTypes: dedupeTypes(normalizedUnsupportedTypes)
  };
}

export function getRouteParam(
  route: ActivatedRoute,
  paramName: string
): string | null {
  let current: ActivatedRoute | null = route;

  while (current) {
    const value = current.snapshot.paramMap.get(paramName);
    if (value) {
      return value;
    }

    current = current.parent;
  }

  return null;
}

export function getClientIdFromRoute(
  route: ActivatedRoute,
  context?: WorkflowContextService
): string | null {
  return getRouteParam(route, 'clientId') || context?.client?.id || null;
}

export function buildHierarchicalActivities(
  summary: MigrationSummary | null
): ActivityChange[] {
  if (!summary) {
    return [];
  }

  const activityMap = new Map<string, ActivityChange>();

  summary.addedActivities?.forEach(activity => {
    if (!activity) return;
    activityMap.set(activity.name, {
      name: activity.name,
      action: 'add',
      dataEntities: []
    });
  });

  summary.removedActivities?.forEach(activity => {
    if (!activity) return;
    activityMap.set(activity.name, {
      name: activity.name,
      action: 'remove',
      dataEntities: []
    });
  });

  summary.addedDataEntities?.forEach(entity => {
    let activity = activityMap.get(entity.activityName);
    if (!activity) {
      activity = {
        name: entity.activityName,
        action: 'add',
        dataEntities: []
      };
      activityMap.set(entity.activityName, activity);
    }

    activity.dataEntities.push({
      templateCode: entity.templateCode,
      action: 'add'
    });
  });

  summary.removedDataEntities?.forEach(entity => {
    let activity = activityMap.get(entity.activityName);
    if (!activity) {
      activity = {
        name: entity.activityName,
        action: 'add',
        dataEntities: []
      };
      activityMap.set(entity.activityName, activity);
    }

    activity.dataEntities.push({
      templateCode: entity.templateCode,
      action: 'remove'
    });
  });

  return Array.from(activityMap.values());
}
